import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Search, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateLong } from "@/lib/format";
import { labelOf, Pager, SelectField, useAssignees, useLookups } from "@/lib/crm";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LeadDialog, PRIORITIES } from "@/components/crm/LeadDialog";
import { LeadSheet } from "@/components/crm/LeadSheet";

const EMPTY = { q: "", status: "", owner_id: "", source: "", priority: "", date_from: "", page: 1 };
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));

export default function LeadsList() {
  const { can } = useAuth();
  const { recordId } = useParams();
  const lk = useLookups();
  const users = useAssignees();
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState(recordId || null);
  const set = (p) => setF((x) => ({ ...x, page: 1, ...p }));
  const load = useCallback(() => {
    const p = clean(f);
    if (p.date_from) p.date_from = new Date(p.date_from).toISOString();
    return api.get("/crm/leads", { params: p }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="leads-page">
      <PageHeader overline="CRM" title="Lead" description="Contatti commerciali da qualificare, con fase: Nuovo, Contattato, Appuntamento (passa in pipeline opportunità)."
        actions={can("leads:create") && <Button onClick={() => setCreateOpen(true)} className="gap-2" data-testid="leads-new-button"><Plus />Nuovo lead</Button>} />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca per azienda, nome, email…" value={f.q} onChange={(e) => set({ q: e.target.value })} data-testid="leads-search" /></div>
        <SelectField value={f.status} onChange={(v) => set({ status: v })} options={lk.lead_status} allowNone="Tutte le fasi" testId="lead-filter-status" className="sm:w-48" />
        <Popover>
          <PopoverTrigger asChild><Button variant="outline" className="gap-2" data-testid="leads-more-filters"><SlidersHorizontal />Filtri</Button></PopoverTrigger>
          <PopoverContent align="end" className="w-72 space-y-3">
            <SelectField label="Responsabile" value={f.owner_id} onChange={(v) => set({ owner_id: v })} options={users} allowNone="Tutti" testId="lead-filter-owner" />
            <SelectField label="Fonte" value={f.source} onChange={(v) => set({ source: v })} options={lk.lead_source} allowNone="Tutte" testId="lead-filter-source" />
            <SelectField label="Priorità" value={f.priority} onChange={(v) => set({ priority: v })} options={PRIORITIES} allowNone="Tutte" testId="lead-filter-priority" />
            <div className="space-y-1.5"><p className="text-xs font-medium">Creati dal</p><Input type="date" value={f.date_from} onChange={(e) => set({ date_from: e.target.value })} data-testid="lead-filter-date" /></div>
          </PopoverContent>
        </Popover>
      </div>
      <Panel className="overflow-x-auto">
        <Table data-testid="leads-table">
          <TableHeader><TableRow><TableHead>Lead</TableHead><TableHead>Fase</TableHead><TableHead>Priorità</TableHead><TableHead>Fonte</TableHead><TableHead>Responsabile</TableHead><TableHead>Creato</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data?.items || []).map((l) => {
              const st = labelOf(lk.lead_status, l.status);
              const pr = labelOf(PRIORITIES, l.priority);
              return (
                <TableRow key={l.id} className="cursor-pointer" onClick={() => setOpenId(l.id)} data-testid={`lead-row-${l.id}`}>
                  <TableCell><p className="font-semibold text-ink">{l.company_name || l.contact_name}</p><p className="text-xs text-slate-500">{l.company_name ? l.contact_name : l.email}</p></TableCell>
                  <TableCell><StatusBadge tone={st.tone} data-testid={`lead-phase-${l.id}`}>{st.label}</StatusBadge></TableCell>
                  <TableCell><StatusBadge tone={pr.tone} dot={false}>{pr.label}</StatusBadge></TableCell>
                  <TableCell className="text-sm text-slate-600">{labelOf(lk.lead_source, l.source).label}</TableCell>
                  <TableCell className="text-sm text-slate-700">{l.owner_name || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDateLong(l.created_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {data?.items.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="leads-empty">Nessun lead trovato.</p>}
      </Panel>
      {data && <Pager page={data.page} pages={data.pages} total={data.total} unit="lead" onPage={(p) => setF((x) => ({ ...x, page: p }))} />}
      <LeadDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load} />
      <LeadSheet leadId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
